import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AutoAttendCancelModal, EndAutoAttendModal, MeetingDetails } from '@copilot/ui';
import EndMeetingModal from '../components/meeting/EndMeetingModal';
import { CO_PILOT_MODES, LANGUAGES } from '../constants/languages';
import { useAutoAttendActions } from '../hooks/useAutoAttendActions';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useEndMeeting } from '../hooks/useEndMeeting';
import * as botMeetingApi from '../services/botMeetingApi';
import { meetingDetailsApi } from '../services/meetingDetailsApi';
import * as meetingApi from '../services/meetingApi';
import { getUserFriendlyError } from '../utils/errors';

const MEETING_STATUS_ENDED = 'ended';

// The Meeting Details UI is shared with Desktop (@copilot/ui's
// MeetingDetails). This page owns loading the meeting (and its Auto
// Attend facts), the background refresh, and ending a running meeting.
function MeetingDetailsPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');

    meetingApi
      .getMeeting(meetingId)
      .then((data) => {
        if (!cancelled) setMeeting(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getUserFriendlyError(err, 'Unable to load meeting details.'));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [meetingId, retryToken]);

  // Auto Attend (bot) meetings only: the bot's own facts, from a separate
  // endpoint. Purely additive - any failure just hides that panel.
  const [botInfo, setBotInfo] = useState(null);
  const isBotMeeting = meeting?.client_type === 'bot';

  useEffect(() => {
    if (!isBotMeeting) {
      setBotInfo(null);
      return undefined;
    }

    let cancelled = false;
    botMeetingApi
      .getBotMeeting(meetingId)
      .then((data) => {
        if (!cancelled) setBotInfo(data);
      })
      .catch(() => {
        if (!cancelled) setBotInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isBotMeeting, meetingId]);

  // Silent re-read of the meeting (and its bot facts). A status change
  // remounts the tabs, which then load the finished data.
  const refreshMeeting = useCallback(
    async ({ isStale }) => {
      const data = await meetingApi.getMeeting(meetingId);
      const bot =
        data?.client_type === 'bot'
          ? await botMeetingApi.getBotMeeting(meetingId).catch(() => undefined)
          : undefined;

      if (isStale()) return;
      setMeeting(data);
      if (bot !== undefined) setBotInfo(bot);
    },
    [meetingId]
  );

  useAutoRefresh(refreshMeeting, { enabled: !isLoading && !error });

  // The same confirm-then-end flow as the Meetings list (hooks/
  // useEndMeeting.js): end, then re-read, so an "already ended" race
  // just lands on Ended.
  const { endTarget, isEnding, endError, requestEnd, cancelEnd, confirmEnd } = useEndMeeting({
    onSettled: ({ ended, meeting: fresh }) => {
      if (fresh) {
        setMeeting(fresh);
      } else if (ended) {
        setMeeting((current) => ({ ...current, status: MEETING_STATUS_ENDED }));
      }
    },
  });

  // An Auto Attend meeting is not ended with the PATCH above (the backend
  // refuses to set a bot meeting's status): a recording bot is asked to
  // leave (End Meeting) and one that has not started recording is
  // cancelled - hooks/useAutoAttendActions.js. Afterwards the meeting and
  // the bot's own facts are re-read, so the page shows what the backend
  // now says (a stop still reads "recording" until the bot is out).
  const {
    stopTarget,
    cancelTarget,
    requestStop,
    requestCancel,
    closeStop,
    closeCancel,
    confirmStop,
    confirmCancel,
    busyId: autoAttendBusyId,
    isStopping,
    isCancelling,
    actionError: autoAttendError,
    leavingIds,
  } = useAutoAttendActions({
    onSettled: ({ meeting: fresh }) => {
      if (fresh) setMeeting(fresh);
      botMeetingApi
        .getBotMeeting(meetingId)
        .then((data) => setBotInfo(data))
        .catch(() => {});
    },
  });

  return (
    <>
      <MeetingDetails
        meetingId={meetingId}
        meeting={meeting}
        isLoading={isLoading}
        error={error}
        onRetry={() => setRetryToken((token) => token + 1)}
        api={meetingDetailsApi}
        languages={LANGUAGES}
        modes={CO_PILOT_MODES}
        autoAttend={botInfo}
        onEndMeeting={() => requestEnd({ id: meetingId })}
        onEndAutoAttend={() => requestStop({ id: meetingId })}
        onCancelAutoAttend={() => requestCancel({ id: meetingId })}
        isEnding={isEnding || autoAttendBusyId !== null}
        isLeaving={leavingIds.includes(meetingId)}
        endError={endError || autoAttendError}
        onBack={() => navigate('/app/history')}
      />

      {endTarget && (
        <EndMeetingModal
          onCancel={cancelEnd}
          onConfirm={confirmEnd}
          isSubmitting={isEnding}
          cancelLabel="Cancel"
        />
      )}

      {stopTarget && (
        <EndAutoAttendModal
          onCancel={closeStop}
          onConfirm={confirmStop}
          isSubmitting={isStopping}
        />
      )}

      {cancelTarget && (
        <AutoAttendCancelModal
          onKeep={closeCancel}
          onConfirm={confirmCancel}
          isSubmitting={isCancelling}
        />
      )}
    </>
  );
}

export default MeetingDetailsPage;
